import { createSlice } from "@reduxjs/toolkit";
import {
  createCampaignThunk,
  fetchJobsThunk,
  fetchJobDetailsThunk,
  fetchJobAuditsThunk,
  cancelJobThunk,
  retryJobThunk,
  validateCustomerIdsThunk,
  fetchSegmentStatsThunk,
  fetchCustomersBySegmentsThunk,
  fetchChannelStatusThunk,
} from "../thunks/campaignThunks";

const initialState = {
  jobs: [],
  selectedJob: null,
  jobAudits: [],
  wizardDraft: {
    step: 1,
    audience: {
      segments: [],
      platforms: ["all"],
      testDevices: [],
    },
    content: {
      templateKey: "",
      templateVersion: null,
      variables: {},
    },
    schedule: {
      sendNow: true,
      scheduledTime: null,
    },
  },
  loading: false,
  error: null,
  pagination: {
    nextCursor: null,
    hasMore: true,
  },

  // Add channel status to the state
  channels: {
    status: {
      FCM: true, // default to enabled until we know otherwise
      APNS: true,
      WEB: true,
      WHATSAPP: false,
    },
    enabledChannels: [],
    loading: false,
    error: null,
  },

  customer: {
    /* segment targeting */
    segmentStats: {},
    customerIds: [], // last fetched list
    isTestMode: false,

    /* validation */
    validation: {
      valid: [],
      invalid: [],
      nonExistent: [],
      summary: null,
    },

    loading: false,
    error: null,
  },
};

const campaignSlice = createSlice({
  name: "notificationCampaigns",
  initialState,
  reducers: {
    updateWizardStep: (state, action) => {
      state.wizardDraft.step = action.payload;
    },
    updateWizardDraft: (state, action) => {
      const { section, data } = action.payload;
      state.wizardDraft[section] = {
        ...state.wizardDraft[section],
        ...data,
      };
    },
    resetWizardDraft: (state) => {
      state.wizardDraft = initialState.wizardDraft;
    },
    clearCampaignError: (state) => {
      state.error = null;
    },
    setSelectedJob: (state, action) => {
      state.selectedJob = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Create campaign
    builder
      .addCase(createCampaignThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCampaignThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      }) // Update the createCampaignThunk.fulfilled case:
      .addCase(createCampaignThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.wizardDraft = initialState.wizardDraft;
        // Store the response for UI to handle queuedUntil
        state.lastCampaignResponse = action.payload;
      });

    // Fetch jobs
    builder
      .addCase(fetchJobsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobsThunk.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.reset) {
          state.jobs = action.payload.jobs;
        } else {
          state.jobs = [...state.jobs, ...action.payload.jobs];
        }
        state.pagination.nextCursor = action.payload.nextCursor;
        state.pagination.hasMore = action.payload.hasMore;
      })
      .addCase(fetchJobsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch job details
    builder
      .addCase(fetchJobDetailsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobDetailsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedJob = action.payload;
      })
      .addCase(fetchJobDetailsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch job audits
    builder
      .addCase(fetchJobAuditsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobAuditsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.jobAudits = action.payload;
      })
      .addCase(fetchJobAuditsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Cancel job
    builder
      .addCase(cancelJobThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelJobThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.jobs.findIndex((j) => j.id === action.payload.id);
        if (index >= 0) {
          state.jobs[index] = action.payload;
        }
        if (state.selectedJob?.id === action.payload.id) {
          state.selectedJob = action.payload;
        }
      })
      .addCase(cancelJobThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Retry job
    builder
      .addCase(retryJobThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(retryJobThunk.fulfilled, (state, action) => {
        state.loading = false;
        // New job created, will be fetched in next refresh
      })
      .addCase(retryJobThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    /* -------------------------------------------------- */
    /* fetch by segments                                  */
    /* -------------------------------------------------- */
    builder
      .addCase(fetchCustomersBySegmentsThunk.pending, (state) => {
        state.customer.loading = true;
        state.customer.error = null;
      })
      .addCase(fetchCustomersBySegmentsThunk.fulfilled, (state, action) => {
        state.customer.loading = false;
        state.customer.customerIds = action.payload.customerIds;
        state.customer.isTestMode = !!action.payload.isTestMode;
      })
      .addCase(fetchCustomersBySegmentsThunk.rejected, (state, action) => {
        state.customer.loading = false;
        state.customer.error = action.payload;
      });

    /* -------------------------------------------------- */
    /* segment stats                                      */
    /* -------------------------------------------------- */
    builder
      .addCase(fetchSegmentStatsThunk.pending, (state) => {
        state.customer.loading = true;
        state.customer.error = null;
      })
      .addCase(fetchSegmentStatsThunk.fulfilled, (state, action) => {
        state.customer.loading = false;
        state.customer.segmentStats = action.payload.stats;
      })
      .addCase(fetchSegmentStatsThunk.rejected, (state, action) => {
        state.customer.loading = false;
        state.customer.error = action.payload;
      });

    /* -------------------------------------------------- */
    /* ID validation                                      */
    /* -------------------------------------------------- */
    builder
      .addCase(validateCustomerIdsThunk.pending, (state) => {
        state.customer.loading = true;
        state.customer.error = null;
      })
      .addCase(validateCustomerIdsThunk.fulfilled, (state, action) => {
        state.customer.loading = false;
        state.customer.validation = {
          valid: action.payload.valid,
          invalid: action.payload.invalid,
          nonExistent: action.payload.nonExistent,
          summary: action.payload.summary,
        };
      })
      .addCase(validateCustomerIdsThunk.rejected, (state, action) => {
        state.customer.loading = false;
        state.customer.error = action.payload;
      });

    /* -------------------------------------------------- */
    /* channel status                                     */
    /* -------------------------------------------------- */
    builder
      .addCase(fetchChannelStatusThunk.pending, (state) => {
        state.channels.loading = true;
        state.channels.error = null;
      })
      .addCase(fetchChannelStatusThunk.fulfilled, (state, action) => {
        state.channels.loading = false;
        state.channels.status = action.payload.channelStatus || {};
        state.channels.enabledChannels = action.payload.enabledChannels || [];
      })
      .addCase(fetchChannelStatusThunk.rejected, (state, action) => {
        state.channels.loading = false;
        state.channels.error = action.payload;
      });
  },
});

export const {
  updateWizardStep,
  updateWizardDraft,
  resetWizardDraft,
  clearCampaignError,
  setSelectedJob,
} = campaignSlice.actions;

export default campaignSlice.reducer;
